import axios, { isAxiosError, AxiosProgressEvent, AxiosError } from 'axios';
import { captureError, Sentry } from '../../utils/SentryHelper';

const base_url: string = process.env.REACT_APP_API_BASE_URL as string;

// axios instance to make n/w requests
export const axiosClient = axios.create({
  baseURL: base_url,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'x-app-origin': window.location.origin,
  },
});

// Request interceptor — attach session_id from localStorage on every request
axiosClient.interceptors.request.use(config => {
  const sessionId = localStorage.getItem('session_id');
  if (sessionId) {
    config.headers['x-session-id'] = sessionId;
  }
  return config;
});

// Response interceptor for axios instance
axiosClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new Event('authentication-management'));
    }
    const status: number = error.response?.status ?? 0;
    // Don't noise Sentry with 401s — those are handled by the auth flow
    if (status !== 401) {
      Sentry.addBreadcrumb({
        category: 'api',
        message:  `${error.config?.method?.toUpperCase()} ${error.config?.url} → ${status}`,
        level:    status >= 500 ? 'error' : 'warning',
        data: {
          status,
          message: error.response?.data?.client_error_msg ?? error.message,
        },
      });
      if (status >= 500) captureError(error, { url: error.config?.url, status });
    }
    return Promise.reject(error);
  }
);

// axios Instance to make n/w requests with form data
export const axiosClientForFormData = axios.create({
  baseURL: base_url,
  withCredentials: true,
  headers: {
    'Content-Type': 'multipart/form-data',
    'x-app-origin': window.location.origin,
  },
  maxBodyLength: Infinity,
  maxContentLength: Infinity,
});

// Response interceptor for axios instance for Form Data
axiosClientForFormData.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new Event('authentication-management'));
    }
    const status: number = error.response?.status ?? 0;
    if (status !== 401) {
      Sentry.addBreadcrumb({
        category: 'api',
        message:  `${error.config?.method?.toUpperCase()} ${error.config?.url} → ${status}`,
        level:    status >= 500 ? 'error' : 'warning',
        data: {
          status,
          message: error.response?.data?.client_error_msg ?? error.message,
        },
      });
      if (status >= 500) captureError(error, { url: error.config?.url, status });
    }
    return Promise.reject(error);
  }
);

// type definition for error
export interface API_Error_Type {
  status?: number;
  client_error_msg: string;
  actual_error_msg?: string;
}

// Handle API errors
const handle_api_error = (error: unknown): never => {
  // Handle errors from API
  if (isAxiosError<API_Error_Type>(error)) {
    // Error response is sent from server
    if (error.response) {
      throw {
        status: error.response.status,
        client_error_msg: error.response.data.client_error_msg,
      } satisfies API_Error_Type;
    }
    // Handle errors from API w/o response
    else {
      throw {
        client_error_msg: error.message,
      } satisfies API_Error_Type;
    }
  }

  // Handle programatical errors
  else if (error instanceof Error) {
    throw {
      client_error_msg: error.message,
    } satisfies API_Error_Type;
  }

  // Handle unknown errors
  else {
    throw {
      client_error_msg: 'Something went wrong',
    } satisfies API_Error_Type;
  }
};

// Handle API call
export const generic_get_network_request = async <T>(
  api_endpoint: string
): Promise<T> => {
  try {
    const response = await axiosClient.get<T>(api_endpoint);
    return response.data;
  } catch (error) {
    handle_api_error(error);
  }

  throw {};
};

// Handle API call
export const generic_patch_network_request = async <T, P>(
  api_endpoint: string,
  api_payload?: P
): Promise<T> => {
  try {
    const response = await axiosClient.patch<T>(api_endpoint, api_payload);
    return response.data;
  } catch (error) {
    handle_api_error(error);
  }

  throw {};
};

// Handle API call
export const generic_post_network_request = async <T, P>(
  api_endpoint: string,
  api_payload?: P,
  is_form_data?: boolean
): Promise<T> => {
  try {
    const response = await axiosClient.post<T>(
      api_endpoint,
      api_payload,
      is_form_data
        ? {
            headers: { 'Content-Type': undefined },
          }
        : {}
    );
    return response.data;
  } catch (error) {
    handle_api_error(error);
  }

  throw {};
};

// Handle API call
export const generic_delete_network_request = async <T>(
  api_endpoint: string
): Promise<T> => {
  try {
    const response = await axiosClient.delete<T>(api_endpoint);
    return response.data;
  } catch (error) {
    handle_api_error(error);
  }

  throw {};
};

// Handle API call
export const aws_put_network_request = async <T, P>(
  api_endpoint: string,
  api_payload?: P,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
): Promise<T> => {
  try {
    const response = await axiosClient.put<T>(api_endpoint, api_payload, {
      withCredentials: false,
      headers: {
        'Content-Type': 'application/octet-stream',
        'x-app-origin': undefined,
        'x-amz-server-side-encryption': 'AES256',
      },
      ...(onUploadProgress ? { onUploadProgress } : {}),
    });
    return response.data;
  } catch (error) {
    const axiosErr = error as AxiosError;
    const status = axiosErr.response?.status ?? 500;
    let code = 'UnknownError';
    let message = axiosErr.message;

    if (axiosErr.response?.data) {
      const parser = new DOMParser();
      const xml = parser.parseFromString(
        axiosErr.response.data as string,
        'application/xml'
      );
      code = xml.querySelector('Code')?.textContent ?? code;
      message = xml.querySelector('Message')?.textContent ?? message;
    }

    throw {
      status,
      client_error_msg: 'Failed to upload file',
      actual_error_msg: `AWS Error - Code: ${code}, Message: ${message}`,
    } satisfies API_Error_Type;
  }

  throw {};
};
